<script setup>
import useFunctions from '@/composables/useFunctions';
import { addContentLogAPI } from '@/api/logServiceApi';

const useFunc = useFunctions();

const props = defineProps({
  lockSubject: { type: Boolean, default: false },
  persons: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['add', 'remove']);

const onClickRemove = (idx) => {
  if (props.lockSubject && idx === 0) return;
  addContentLogAPI({ contentType: 'ap_harmony_btn_remove_person' });
  emit('remove', idx);
};

const getKoreanAge = (person) => {
  if (!person?.solar) return person?.age || null;
  const year = Number(person.solar.split('-')[0]);
  return year ? new Date().getFullYear() - year + 1 : null;
};

const getPersonDisplay = (person) => {
  if (!person) return {};
  const gender = useFunc.getGenderTxt(person.gender);
  const koreanAge = getKoreanAge(person);
  const age = koreanAge ? `${koreanAge}세` : '';
  const solar = person.solar ? useFunc.formatCustomDate(person.solar) : '';
  return {
    name: person.guestUserName || person.userName || person.name || '-',
    gender,
    age,
    solar,
  };
};

/** 인원 수에 따른 원 크기 (arena 너비 대비 %) */
const circleSizePct = computed(() => {
  const count = props.persons.length;
  if (count <= 2) return 40;
  if (count === 3) return 40;
  if (count === 4) return 40;
  return 32;
});

/** 인원 수별 위치 (%, %) */
const positionMap = {
  2: [
    { x: 22, y: 25 },
    { x: 78, y: 25 },
  ],
  3: [
    { x: 22, y: 25 },
    { x: 78, y: 25 },
    { x: 50, y: 74.5 },
  ],
  4: [
    { x: 22, y: 25 },
    { x: 78, y: 25 },
    { x: 22, y: 74.5 },
    { x: 78, y: 74.5 },
  ],
  5: [
    { x: 50, y: 18 },
    { x: 83, y: 43 },
    { x: 70, y: 82 },
    { x: 30, y: 82 },
    { x: 17, y: 43 },
  ],
};

/** 인원 수별 연결선 (인덱스 쌍) */
const edgeMap = {
  2: [[0, 1]],
  3: [
    [0, 1],
    [1, 2],
    [2, 0],
  ],
  4: [
    [0, 1],
    [1, 3],
    [3, 2],
    [2, 0],
  ],
  5: [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 4],
    [4, 0],
  ],
};

/** 슬롯 배경색: 1,4번(idx 0,3) = 블루, 2,3,5번(idx 1,2,4) = 핑크 */
const slotColors = ['#ECF2FF', '#FFF1FD', '#FFF1FD', '#ECF2FF', '#FFF1FD'];
const getSlotColor = (idx) => slotColors[idx] || '#FFF1FD';

const positions = computed(() => {
  const count = props.persons.length;
  return positionMap[count] || positionMap[2];
});

const edges = computed(() => {
  const count = props.persons.length;
  return edgeMap[count] || edgeMap[2];
});
</script>

<template>
  <div class="harmony-person">
    <div class="harmony-step">
      <span class="harmony-step__label">STEP 2</span>
      <span class="harmony-step__text">대상자를 선택해주세요</span>
    </div>
    <div class="harmony-person__desc">
      <p>＊궁합은 기본 2명으로 볼 수 있으며, <span class="highlight">'나를 중심'</span>으로 봅니다.</p>
      <p>＊여러 명과의 궁합이 필요하다면 대상자를 추가해보세요.</p>
      <p v-if="!lockSubject">＊주체자가 달라지면 새로운 궁합 서비스가 시작됩니다.</p>
      <p v-if="!lockSubject" class="indent">(주체자 변경 시 별도 이용권이 필요해요.)</p>
      <p class="hanging">＊낭만궁합은 두 사람의 사주 전체 흐름을 함께 분석하는 서비스로,<br class="pc-br"><span class="pc-indent">나와 상대방 모두의 <span class="highlight">출생시간(태어난 시)</span> 정보가 필요합니다.</span></p>
    </div>

    <div
      class="harmony-person__arena"
      :style="{
        '--circle-size': circleSizePct + '%',
        '--remove-offset': circleSizePct * 0.15 + '%',
      }"
    >
      <!-- 연결선 -->
      <svg class="harmony-person__svg">
        <line
          v-for="(edge, i) in edges"
          :key="i"
          :x1="positions[edge[0]].x + '%'"
          :y1="positions[edge[0]].y + '%'"
          :x2="positions[edge[1]].x + '%'"
          :y2="positions[edge[1]].y + '%'"
          stroke="var(--Gray-500, #949494)"
          stroke-width="1.5"
        />
      </svg>

      <!-- 대상자 노드 -->
      <div
        v-for="(person, idx) in persons"
        :key="idx"
        class="harmony-person__node"
        :style="{
          left: positions[idx].x + '%',
          top: positions[idx].y + '%',
          background: getSlotColor(idx),
        }"
      >
        <!-- 주체자 뱃지 -->
        <span v-if="idx === 0" class="harmony-person__badge">주체자</span>

        <!-- 선택된 대상자 -->
        <template v-if="person">
          <button v-if="!lockSubject || idx !== 0" class="harmony-person__remove" @click="onClickRemove(idx)"></button>
          <span class="harmony-person__name">{{ getPersonDisplay(person).name }}</span>
          <span class="harmony-person__info">
            {{ getPersonDisplay(person).gender }}
            {{ getPersonDisplay(person).age }}
          </span>
          <span class="harmony-person__date">{{ getPersonDisplay(person).solar }}</span>
        </template>

        <!-- 빈 슬롯 -->
        <template v-else>
          <div class="harmony-person__empty" @click="(!lockSubject || idx !== 0) && emit('add', idx)">
            <span class="harmony-person__label">{{ idx === 0 ? '나' : '대상자' }}</span>
            <i class="harmony-person__plus"></i>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.harmony-step {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  &__label {
    color: var(--Orange-400, #ff6b6b);
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
  }

  &__text {
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;
  }
}

.harmony-person__desc {
  margin-bottom: 24px;
  p {
    color: var(--Gray-700, #575757);
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%;

    .highlight {
      color: var(--orange-300-main, #eb4c10);
      font-weight: 500;
    }

    &.indent {
      padding-left: 12px;
    }

    @media (max-width: 768px) {
      &.hanging {
        padding-left: 12px;
        text-indent: -12px;
      }
    }

    .pc-br {
      display: none;
    }

    .pc-indent {
      display: inline;
    }

    @media (min-width: 769px) {
      .pc-br {
        display: inline;
      }

      .pc-indent {
        display: inline-block;
        padding-left: 12px;
      }
    }
  }
}

.harmony-person__arena {
  position: relative;
  width: 100%;
  max-width: 420px;
  aspect-ratio: 420 / 400;
  margin: 0 auto 24px;
}

.harmony-person__svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

}

.harmony-person__node {
  position: absolute;
  transform: translate(-50%, -50%);
  width: var(--circle-size, 38%);
  max-width: 160px;
  aspect-ratio: 1;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.3s ease;
}

.harmony-person__badge {
  position: absolute;
  top: calc(var(--remove-offset, 5%) + 4px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 10px;
  border-radius: 10px;
  border: 1px solid rgba(0, 53, 169, 0.25);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  color: #3366CC;
  font-family: Pretendard;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  z-index: 1;
}

.harmony-person__remove {
  position: absolute;
  top: var(--remove-offset, 5%);
  right: var(--remove-offset, 5%);
  width: clamp(20px, 15%, 24px);
  height: clamp(20px, 15%, 24px);
  border: none;
  padding: 0;
  background: url('@/assets/images_manse/nangman-harmony/icon_remove.svg') center center no-repeat;
  background-size: contain;
  cursor: pointer;
  z-index: 1;
}

.harmony-person__name {
  margin-bottom: 4px;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--Gray-800, #333);
  font-size: 15px;
  font-weight: 700;
  line-height: 22px;
}

.harmony-person__info {
  color: var(--Gray-600, #757575);
  font-size: 12px;
  line-height: 14px;
}

.harmony-person__date {
  color: var(--Gray-600, #757575);
  font-size: 12px;
  line-height: 14px;
}

.harmony-person__empty {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
}

.harmony-person__label {
  text-align: center;
  color: var(--Gray-800, #333);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.harmony-person__plus {
  display: flex;
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  background: url('@/assets/images_manse/nangman-harmony/icon_add.svg') center center no-repeat;
  background-size: 19px 19px;
}
</style>
